import {
  Box,
  Button,
  Dialog,
  IconButton,
  DialogTitle,
  DialogActions,
  Grid,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Toast } from '@components';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Agency, AgencyData } from '../../../utils/types';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import {
  createAgency,
  getAgencyStateOptions,
  updateAgency
} from '../../../networkRequest/agenciesService';
import { useUser } from '../../../hooks/useUser';
import ProtectedElement from '../../../auth/ProtectedElement';
import { agencyInfoEditRoles, checkUserAuthorized, surepassAdminDashboardRoles } from '../../../utils/authorization';
import { createOptionalPhoneValidator } from '../../../utils/validationTools';

type AgencyDialogFormProps = {
  open: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>;
  handleResponse: (agency: AgencyData) => void;
  agency?: Agency;
};

type EmailDomain = { value: string; isNew: boolean };

const parseDomains = (emailDomain?: string): EmailDomain[] => {
  const domains = (emailDomain || '')
    .split(',')
    .map((domain) => domain.trim())
    .filter(Boolean);
  return (domains.length ? domains : ['']).map((value) => ({ value, isNew: false }));
};

const contactPhoneSchema = createOptionalPhoneValidator();

const clearableFields: (keyof Agency)[] = ['contact_email', 'contact_phone'];

const AgencyDialogForm = ({
  open,
  setDialogOpen,
  setSuccessMessage,
  handleResponse,
  agency
}: AgencyDialogFormProps) => {
  const theme = useTheme();
  const padding = theme.spacing(3);
  const [errorMessage, setErrorMessage] = useState('');
  const [domains, setDomains] = useState<EmailDomain[]>(() => parseDomains(agency?.email_domain));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const [contactPhoneError, setContactPhoneError] = useState('');
  const { user } = useUser();

  const isSuperAdmin = checkUserAuthorized(user!.profile, surepassAdminDashboardRoles);

  // The API rejects changes to an existing agency's state or organization type from
  // anyone but a Super Admin.
  const canEditStateAndOrganizationType = !agency?.id || isSuperAdmin;

  useEffect(() => {
    if (!open || !canEditStateAndOrganizationType) return;

    const loadStateOptions = async () => {
      const { data, error } = await getAgencyStateOptions();
      if (data) setStateOptions(data);
      if (error) setErrorMessage(error);
    };
    loadStateOptions();
  }, [open, canEditStateAndOrganizationType]);

  // Keep the saved state selectable even before the options load, or if they fail to.
  const stateSelectOptions =
    agency?.state && !stateOptions.includes(agency.state)
      ? [agency.state, ...stateOptions]
      : stateOptions;

  const updateDomain = (index: number, value: string) => {
    setDomains((prev) => prev.map((domain, i) => (i === index ? { ...domain, value } : domain)));
  };

  const addDomain = () => setDomains((prev) => [...prev, { value: '', isNew: true }]);

  const removeDomain = (index: number) => {
    setDomains((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!user) {
      throw new Error('User not found');
    }

    const form = e.currentTarget;
    const formData = new FormData(form);

    const contactPhone = formData.get('contact_phone')?.toString().trim() ?? '';
    const contactPhoneResult = contactPhoneSchema.safeParse(contactPhone);
    if (!contactPhoneResult.success) {
      setContactPhoneError(contactPhoneResult.error.errors[0].message);
      return;
    }

    const rawPayload: Partial<AgencyData> = {
      name: formData.get('name')?.toString().trim(),
      email_domain: domains
        .map((domain) => domain.value.trim())
        .filter(Boolean)
        .join(', '),
      contact_email: formData.get('contact_email')?.toString().trim(),
      contact_phone: contactPhone,
      ...(canEditStateAndOrganizationType && {
        kind: formData.get('kind')?.toString().trim(),
        state: formData.get('state')?.toString().trim()
      })
    };

    // An emptied contact field is sent only when it clears a value the agency has saved.
    const payload = Object.fromEntries(
      Object.entries(rawPayload).filter(([key, value]) => {
        const field = key as keyof Agency;
        return (
          value != null && (value !== '' || (clearableFields.includes(field) && !!agency?.[field]))
        );
      })
    );

    setIsSubmitting(true);
    try {
      let response;
      if (agency?.id) {
        response = await updateAgency(user?.accessToken, {
          id: agency.id,
          ...payload
        });
      } else {
        response = await createAgency(user?.accessToken, { agency: payload });
      }
      if (response.error) {
        setErrorMessage(response.error);
      } else {
        setSuccessMessage(
          `Successfully ${agency?.id ? 'Updated' : 'Created'} Agency${agency?.id ? '' : ' ' + payload.name}`
        );
        handleResponse(response.data);
        setDialogOpen(false);
      }
    } catch (error: any) {
      if (error?.message) {
        console.error(error);
        setErrorMessage(error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const textFieldSx = {
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: theme.palette.primary.dark
    }
  };

  const emailDomainFields = (
    <>
      <Grid size={12} key="email-domain-section">
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            color: theme.palette.text.primary,
            mb: 0.5
          }}>
          Email Domain
        </Typography>
        <Typography sx={{ fontSize: '14px', color: theme.palette.text.primary, mb: 2 }}>
          Configure your organization&rsquo;s approved email domains. SurePass uses this list to
          verify new users. Those with matching domains pass the first verification layer
          automatically.
        </Typography>
      </Grid>
      {domains.map((domain, index) => {
        // Owners can add domains and remove ones they just added before saving, but editing or
        // deleting an already-saved domain is Super Admin only.
        const canEditDomain = !agency?.id || isSuperAdmin || domain.isNew;
        const canDeleteDomain = !agency?.id || isSuperAdmin || domain.isNew;
        return (
          <Grid size={12} key={`domain-${index}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label={index === 0 ? 'Primary Email Domain' : 'Email Domain'}
                sx={{ ...textFieldSx, flex: 1 }}
                placeholder={index === 0 ? 'Primary Email Domain' : 'Email Domain'}
                slotProps={{
                  inputLabel: {
                    shrink: true
                  }
                }}
                fullWidth
                required={index === 0 && !agency?.id}
                disabled={!canEditDomain}
                value={domain.value}
                onChange={(e) => updateDomain(index, e.target.value)}
              />
              {index > 0 && canDeleteDomain && (
                <IconButton
                  aria-label={`Delete Email Domain ${domain.value || index}`}
                  onClick={() => removeDomain(index)}
                  sx={{
                    color: theme.palette.text.primary,
                    width: '36px',
                    height: '30px'
                  }}>
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          </Grid>
        );
      })}
      <Grid size={12} key="add-email-domain">
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={addDomain}
          sx={{ fontWeight: 'bold' }}>
          Email Domain
        </Button>
      </Grid>
    </>
  );

  return (
    <>
      {errorMessage && (
        <Toast type="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}
      <Dialog
        open={open}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{ backdrop: { invisible: true }, paper: { style: { borderRadius: 8 } } }}>
        <IconButton
          aria-label="close"
          onClick={() => setDialogOpen(false)}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500]
          })}>
          <CloseIcon sx={{ fontSize: 32, color: theme.palette.grey[600] }} />
        </IconButton>
        <DialogTitle sx={{ padding }}>{agency?.id ? 'Edit Organization' : 'Add Agency'}</DialogTitle>
        <form onSubmit={handleSubmit} role="form">
          <DialogContent sx={{ padding }}>
            <Grid container spacing={3}>
              <Grid size={12} key="name">
                <TextField
                  label="Name"
                  sx={textFieldSx}
                  name="name"
                  placeholder="Name"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  fullWidth
                  required={agency?.id ? false : true}
                  defaultValue={agency?.name}
                />
              </Grid>
              <Grid size={12} key="agency-kind-select-control">
                <FormControl
                  fullWidth
                  variant="outlined"
                  required={agency?.id ? false : true}
                  disabled={!canEditStateAndOrganizationType}>
                  <InputLabel
                    id="agency-kind-select-label"
                    sx={{ '&.MuiInputLabel-outlined': { color: theme.palette.primary.dark } }}>
                    Organization Type
                  </InputLabel>
                  <Select
                    labelId="agency-kind-select-label"
                    id="agency-kind-select"
                    data-testid="agency-kind-select"
                    name="kind"
                    displayEmpty
                    label="Organization Type"
                    defaultValue={agency?.kind}>
                    <MenuItem value="" disabled></MenuItem>
                    <MenuItem value="law_enforcement">Law Enforcement</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12} key="agency-state-select-control">
                <FormControl
                  fullWidth
                  variant="outlined"
                  disabled={!canEditStateAndOrganizationType}>
                  <InputLabel
                    id="agency-state-select-label"
                    sx={{ '&.MuiInputLabel-outlined': { color: theme.palette.primary.dark } }}>
                    State
                  </InputLabel>
                  <Select
                    labelId="agency-state-select-label"
                    id="agency-state-select"
                    data-testid="agency-state-select"
                    name="state"
                    displayEmpty
                    label="State"
                    defaultValue={agency?.state ?? ''}
                    MenuProps={{ slotProps: { paper: { style: { maxHeight: 320 } } } }}>
                    <MenuItem value="" disabled></MenuItem>
                    {stateSelectOptions.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12} key="contact_email">
                <TextField
                  label="Contact Email"
                  sx={textFieldSx}
                  name="contact_email"
                  placeholder="Contact Email"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  fullWidth
                  defaultValue={agency?.contact_email}
                />
              </Grid>
              <Grid size={12} key="contact_phone">
                <TextField
                  label="Contact Phone"
                  sx={textFieldSx}
                  name="contact_phone"
                  placeholder="(555) 123-4567"
                  type="tel"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  fullWidth
                  defaultValue={agency?.contact_phone}
                  error={!!contactPhoneError}
                  helperText={contactPhoneError}
                  onChange={() => setContactPhoneError('')}
                />
              </Grid>
              {agency?.id ? (
                <ProtectedElement userProfile={user!.profile} allowedRoles={agencyInfoEditRoles}>
                  {emailDomainFields}
                </ProtectedElement>
              ) : (
                emailDomainFields
              )}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ padding }}>
            <SecondaryButton action={() => setDialogOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
              minHeight="2.25rem"
              minWidth="7rem">
              Save
            </PrimaryButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default AgencyDialogForm;
